import Alert from "@/common/components/Alert";
import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import Select from "@/common/components/Select";
import { useProductCategories } from "@/common/hooks/queries/useProductCategories";
import { useProducts } from "@/common/hooks/queries/useProducts";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { Option } from "@/common/types/option";
import { ProductData } from "@/common/types/product.type";
import { ProductCategoryData } from "@/common/types/productCategory.type";
import { BillDynamicGroupablesUpdateValidationSchema } from "@/features/privateCompanies/schemas/billDynamicGroupables/update.schema";
import { AlertCircleIcon, SearchIcon, TrashIcon, XIcon } from "lucide-react";
import { FieldArray } from "react-hook-form";

interface BillDynamicGroupableItem {
  group: FieldArray<BillDynamicGroupablesUpdateValidationSchema, 'groupable'>;
  index: number;
  updateGroup: (value: FieldArray<BillDynamicGroupablesUpdateValidationSchema, 'groupable'>) => void;
  removeGroup: (index: number) => void;
  selectedCategories: string[];
  selectedProducts: string[];
}

const BillingDynamicGroupableItem = ({ group, updateGroup, removeGroup, index, selectedCategories, selectedProducts }: BillDynamicGroupableItem) => {
  const [searchCategory, setSearchCategory] = useDebounce('');
  const [searchProduct, setSearchProduct] = useDebounce('');

  const { data: productCategoriesOptions } = useProductCategories({
    filters: {
      search: searchCategory,
    },
    mapper: Mapper.mapToOptions<ProductCategoryData>({ valueFieldName: 'id', labelFieldName: 'display_name' }),
  });

  const { data: productsOptions } = useProducts({
    filters: {
      search: searchProduct,
    },
    mapper: Mapper.mapToOptions<ProductData>({ valueFieldName: 'id', labelFieldName: 'display_name' }),
  });

  return (
    <Box className="p-4 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p>
          Fatura {index + 1}
        </p>

        <Button
          name={`remove_groupable_${index}`}
          variant="error"
          onClick={() => removeGroup(index)}
        >
          Deletar

          <TrashIcon />
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Select
          name={`add_groupable_category_${index}`}
          label="Serviços"
          placeholder={(
            <div className="flex items-center gap-x-2 text-white-800">
              <SearchIcon strokeWidth={1.5} className="size-5" />

              <p className="text-sm text-white-300">
                Busque por Serviços
              </p>
            </div>
          )}
          className="w-full"
          onInputChange={setSearchCategory}
          options={productCategoriesOptions.filter(({ value }) => !selectedCategories.includes(value))}
          value=""
          onChange={(newCategory) => {
            updateGroup({
              id: group.id,
              categories: [...(group.categories), newCategory as Option],
              products: group.products,
            });
          }}
        />

        <Select
          name={`add_groupable_product_${index}`}
          label="Produtos"
          placeholder={(
            <div className="flex items-center gap-x-2 text-white-800">
              <SearchIcon strokeWidth={1.5} className="size-5" />

              <p className="text-sm text-white-300">
                Busque por Produtos
              </p>
            </div>
          )}
          className="w-full"
          onInputChange={setSearchProduct}
          options={productsOptions.filter(({ value }) => !selectedProducts.includes(value))}
          value=""
          onChange={(newProduct) => {
            updateGroup({
              id: group.id,
              categories: group.categories,
              products: [...(group.products), newProduct as Option],
            });
          }}
        />
      </div>

      <Alert
        variant="warning"
        Icon={AlertCircleIcon}
        containerClassName="bg-transparent p-0"
      >
        <div className="flex flex-col">
          <p className="text-xs">
            Todos os Produtos do Serviço selecionado serão faturados juntos, caso deseje separar algum poderá criar um novo agrupamento incluindo o produto desejado.
          </p>

          <p className="text-xs italic font-light">
            Atente-se, os Serviços e Produtos que forem específicados em faturas dinâmicas não entrarão nas demais faturas.
          </p>
        </div>
      </Alert>

      {group.categories.map((category, categoryIndex) => (
        <div
          key={`${index}_category_${categoryIndex}`}
          className="flex items-center gap-3"
          data-testid={`groupable_${index}_${categoryIndex}_category`}
        >
          <Button
            name="remove_category"
            variant="link-white"
            className="p-0"
            onClick={() => {
              updateGroup({
                id: group.id,
                categories: group.categories.filter(({ value }) => value !== category.value),
                products: group.products,
              });
            }}
          >
            <XIcon strokeWidth={1.5} size={20} className="text-red-500" />
          </Button>

          <div>
            <p className="italic text-xs">
              Serviço
            </p>

            <p className="text-sm">
              {category.label}
            </p>
          </div>
        </div>
      ))}

      {group.products.map((product, productIndex) => (
        <div
          key={`${index}_product_${productIndex}`}
          className="flex items-center gap-3"
          data-testid={`groupable_${index}_${productIndex}_product`}
        >
          <Button
            name="remove_product"
            variant="link-white"
            className="p-0"
            onClick={() => {
              updateGroup({
                id: group.id,
                categories: group.categories,
                products: group.products.filter(({ value }) => value !== product.value),
              });
            }}
          >
            <XIcon strokeWidth={1.5} size={20} className="text-red-500" />
          </Button>

          <div>
            <p className="italic text-xs">
              Produto
            </p>

            <p className="text-sm">
              {product.label}
            </p>
          </div>
        </div>
      ))}
    </Box>
  );
}

export default BillingDynamicGroupableItem;
